import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Typeahead } from '@/components/ui/typeahead';
import { LoadingPage } from '@/components/ui/loading';
import { Button } from '@/components/ui/button';
import { getProperties, createProperty } from '@/features/properties/api/properties-api';
import { PropertyModal } from '@/features/properties/components/property-modal';
import { useDataCache } from '@/store/data-cache-context';
import type { Property, CreatePropertyRequest } from '@/types/api';

// Single entry point for "Add Devices" — replaces the four buttons that
// used to feed four parallel single-device wizard paths. When entered with
// ?propertyId=<id> we redirect straight to the property's existing setup
// mode (which already handles building picker + bulk scan-in + green/red
// dot grid). When entered without a property in scope, we surface a
// picker first so dashboard / Devices-page entries don't crash on a
// hardcoded route.
//
// Tanner's May 4 feedback: if the user types a property name that doesn't
// exist yet, give them an inline "+ Create new property" affordance so
// the no-property-yet case stops being a dead end. After create, we land
// directly on the new property's setup mode — that's the path Mike wants
// every device addition to flow through.
export function BulkSetupModePage() {
  const navigate = useNavigate();
  const { invalidate } = useDataCache();
  const [searchParams] = useSearchParams();
  const initialPropertyId = searchParams.get('propertyId') ?? '';

  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(!initialPropertyId);
  const [inputValue, setInputValue] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitialName, setCreateInitialName] = useState('');

  // If propertyId is already in the URL, skip the picker and route the
  // user directly into the property portal's setup mode.
  useEffect(() => {
    if (initialPropertyId) {
      navigate(`/properties/${initialPropertyId}?mode=setup`, { replace: true });
    }
  }, [initialPropertyId, navigate]);

  // Load properties for the picker only when we need it.
  useEffect(() => {
    if (initialPropertyId) return;
    getProperties({ perPage: 100 })
      .then((res) => setProperties(res.items))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProperties(false));
  }, [initialPropertyId]);

  // Filter the option list by the typed query so the dropdown shows what
  // the user is searching for. The Typeahead also offers the "+ Create"
  // row when nothing matches.
  const propertyOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    const filtered = q.length === 0
      ? properties
      : properties.filter((p) => p.name.toLowerCase().includes(q));
    return filtered.map((p) => ({ value: p.id, label: p.name }));
  }, [properties, inputValue]);

  if (initialPropertyId) {
    // Redirecting — render nothing so we don't flash the picker.
    return <LoadingPage />;
  }

  if (loadingProperties) return <LoadingPage />;

  function handleSelect(option: { value: string; label: string }) {
    setSelectedPropertyId(option.value);
    setInputValue(option.label);
  }

  function handleCreateNew(text: string) {
    setCreateInitialName(text);
    setShowCreateModal(true);
  }

  async function handleSaveProperty(data: CreatePropertyRequest) {
    const res = await createProperty(data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Failed to create property');
    }
    invalidate(['properties']);
    setShowCreateModal(false);
    // Drop the user directly into the new property's setup mode — this
    // is the "we get routed to the property page, then add a device"
    // path Tanner described in the meeting.
    navigate(`/properties/${res.data.id}?mode=setup`);
  }

  function handleContinue() {
    if (!selectedPropertyId) return;
    navigate(`/properties/${selectedPropertyId}?mode=setup`);
  }

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-md mx-auto">
          <Card variant="elevated" className="p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Devices</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Pick the property you want to add meters to — or type a new
                  name to create one. You'll choose the building and scan in
                  your meters on the next screen.
                </p>
              </div>
            </div>

            <Typeahead
              label="Property"
              required
              placeholder="Search or type a new property name…"
              inputValue={inputValue}
              onInputChange={(text) => {
                setInputValue(text);
                // Typing after a selection clears the selection so the
                // dropdown re-opens to confirm the new query.
                if (selectedPropertyId) setSelectedPropertyId('');
              }}
              onSelect={handleSelect}
              onCreateNew={handleCreateNew}
              options={propertyOptions}
              selectedId={selectedPropertyId}
              allowCreate
              createLabel="Create new property"
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="primary" onClick={handleContinue} disabled={!selectedPropertyId}>
                Continue
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {showCreateModal && (
        <PropertyModal
          property={null}
          initialName={createInitialName}
          onClose={() => setShowCreateModal(false)}
          onSave={handleSaveProperty}
        />
      )}
    </>
  );
}
